import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { get } from 'lodash';

import { SalesOrderModel } from 'projects/workspace/src/app/sales-order/models/sales-order.model';
import { CountryModel } from '../../../rnpl-common';
import { CustomerTypeEnum, DeliveryTypesEnum, PaymentMethodsEnum, PaymentTermsEnum, UIStatesEnum } from '../../../../models';
import { convertAddressToStringHelper } from '../../../rnpl-common/helpers';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { ToasterService } from '../../../ui-components/toaster';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { selectCountries, selectSalesOrder, selectSalesOrderCurrentState } from 'projects/workspace/src/app/sales-order/store/selectors';
import { FormInputChangedModel } from 'projects/workspace/src/app/shared/models';
import { SalesOrderFormsService } from 'projects/workspace/src/app/sales-order/services/sales-order-forms.service';
import { AddressTypeEnum } from 'projects/workspace/src/app/sales-order/enums/address-type.enum';
// import { ModalNameEnum } from '../../../../models/modal-name.enum';
import { PartnersTypeEnum } from 'projects/workspace/src/app/partners/corporate/enums';
import { CommonModalsActionsEnum, ConfirmModalComponent } from '../../modals-common';
import { UPDATE_SMALL_BUSINESS_MODAL_CONFIG } from 'projects/workspace/src/app/shared/constants';
import { selectAccountingSettings } from 'projects/workspace/src/app/store/selectors/shared.selectors';
import { AccountingSettingsModel } from 'projects/workspace/src/app/accounting/accounting-settings-module/models';

@Component({
  selector: 'rnpl-sales-order-billing-info-modal',
  templateUrl: './sales-order-billing-info-modal.component.html',
})
export class SalesOrderBillingInfoModalComponent implements OnInit, OnDestroy {

  public accountingSettings: AccountingSettingsModel;
  public salesOrder: SalesOrderModel;
  public countries: CountryModel[];

  public readonly paymentTerms = PaymentTermsEnum;
  public readonly paymentMethods = PaymentMethodsEnum;
  public readonly deliveryTypes = DeliveryTypesEnum;
  public isReadonly: boolean = true;
  public addressTypeEnum: typeof AddressTypeEnum = AddressTypeEnum;
  public addressTypeList = [];
  public addressTypeForGeneralPartner = [
    {value: AddressTypeEnum.USER_DEFINED, label: 'COMMON.NEW_ADDRESS'},
    {value: AddressTypeEnum.SIMPLIFIED, label: 'COMMON.USER_DEFINED_ADDRESS'},
  ];

  // public modalNameEnum: typeof ModalNameEnum = ModalNameEnum;

  private convertAddressToString = convertAddressToStringHelper;

  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private readonly salesOrderApiService: SalesOrderApiService,
    private readonly toasterService: ToasterService,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    public dialogRef: MatDialogRef<SalesOrderBillingInfoModalComponent>,
    private salesOrderFormsService: SalesOrderFormsService,
    @Inject(MAT_DIALOG_DATA) public data: {
      form: FormGroup
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.store.select(selectAccountingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountingSettings: AccountingSettingsModel) => this.accountingSettings = accountingSettings);

    this.selectCountries();

    this.store.select(selectSalesOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((salesOrder: SalesOrderModel) => {
        this.salesOrder = salesOrder;
        this.addressTypeList = [
          {value: AddressTypeEnum.BILLING, label: 'FORM.BILLING_ADDRESS', enabled: this.isCustomerSelected},
          {value: AddressTypeEnum.DELIVERY, label: 'FORM.DELIVERY_ADDRESS', enabled: this.isCustomerSelected},
          {value: AddressTypeEnum.USER_DEFINED, label: 'COMMON.NEW_ADDRESS', enabled: true},
          {value: AddressTypeEnum.SIMPLIFIED, label: 'COMMON.USER_DEFINED_ADDRESS', enabled: true},
          {value: AddressTypeEnum.BRANCH_OFFICE, label: this.salesOrder.properties.branchOfficeName + ' address', enabled: !!this.salesOrder.properties.branchOfficeId},
        ].filter(i => i.enabled);

        this.updateFormValid();
        this.setFormsState();

        this.salesOrderFormsService.initBillingInfoForm(salesOrder);
        this.data.form = this.salesOrderFormsService.billingInfoForm;
      });

    this.store.select(selectSalesOrderCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: UIStatesEnum) => {
        this.isReadonly = response === UIStatesEnum.VIEW;
        this.setFormsState();
      });
  }

  private updateFormValid(): void {
    if (get(this.salesOrder, 'billingInformation.sconto.enabled')) {
      this.scontoRateControl.setValidators(Validators.required);
      this.scontoPeriodControl.setValidators(Validators.required);
    } else {
      this.scontoRateControl.clearValidators();
      this.scontoPeriodControl.clearValidators();
    }
    this.scontoRateControl.updateValueAndValidity();
    this.scontoPeriodControl.updateValueAndValidity();
  }

  private setFormsState(): void {
    const opts = { onlySelf: true, emitEvent: false };
    if (this.isReadonly) {
      this.data.form.disable(opts);
    } else {
      this.data.form.enable(opts);
    }

    if (this.salesOrder.billingInformation.sameAsDeliveryAddress) {
      this.billingAddressFormGroup.disable(opts);
      this.nameControl.disable(opts);
    }

    if (!get(this.salesOrder, 'properties.company.id')) {
      this.billingAddressFormGroup.disable(opts);
      this.addressTemplateControl.disable(opts);
    }

    if ((get(this.salesOrder, 'properties.partnerType') === PartnersTypeEnum.CORPORATE) || this.isGeneralPartner) {
      this.nameControl.disable(opts);
    }

    if (this.salesOrder.deliveryInformation.deliveryType === this.deliveryTypes.PICK_UP) {
      this.data.form.get('sameAsDeliveryAddress').disable(opts);
    }

    if (this.salesOrder.billingInformation.bankAccount.id) {
      this.ibanControl.disable(opts);
      this.bicControl.disable(opts);
    }

    if (this.salesOrder.properties.instantSale) {
      this.scontoFormGroup.disable(opts);
    }

    if (get(this.salesOrder, 'properties.company.generalType') === 'inland') {
      this.billingAddressFormGroup.get('country_iso3').disable(opts);
    }

    if (this.salesOrder.properties.smallBusiness) {
      this.vatDisabled.disable(opts);
    }
  }

  public updateSalesOrder(fieldValue, fieldName: string): void {
    this.salesOrderApiService.updateSalesOrder(this.salesOrder.id, {fieldValue, fieldName})
      .subscribe(); // updated via store
  }

  public updateSmallBusiness(fieldValue, fieldName: string): void {
    if (this.accountingSettings.smallBusiness.forward === fieldValue) {
      this.updateSalesOrder(fieldValue, fieldName);
      return;
    }

    const dialog = this.dialog.open(ConfirmModalComponent, UPDATE_SMALL_BUSINESS_MODAL_CONFIG);

    dialog.afterClosed().subscribe((res: CommonModalsActionsEnum) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.updateSalesOrder(fieldValue, fieldName);
      } else if (res === CommonModalsActionsEnum.REJECT) {
        this.router.navigate(['/accounting/settings/general-settings']);
      } else {
        this.smallBusiness.setValue(this.salesOrder.properties.smallBusiness);
      }
    });
  }

  public updateAddressField(field: FormInputChangedModel): void {
    const fieldName = 'billingInformation.billingAddress.' + field.fieldName;
    this.updateSalesOrder(field.fieldValue, fieldName);
  }

  public scontoFieldUpdated(field: FormInputChangedModel): void {
    const fieldName = 'billingInformation.sconto.' + field.fieldName;
    this.updateSalesOrder(field.fieldValue, fieldName);
  }

  // public updateBillingInfoAddressByCustomer(): void {
  //   this.salesOrderApiService.updateBillingInfoAddressByCustomer(this.salesOrder.id)
  //     .subscribe();
  // }

  private selectCountries() {
    this.store.select(selectCountries)
      .pipe(takeUntil(this.destroy$))
      .subscribe((countriesFromStore: CountryModel[]) => {
        this.countries = countriesFromStore;
      });
  }

  public showMsg(type: string, message: string): void {
    this.toasterService.notify({ type, message });
  }

  public editAddress(): void {
    this.addressTemplateControl.setValue(AddressTypeEnum.USER_DEFINED);
    this.updateSalesOrder(AddressTypeEnum.USER_DEFINED, 'billingInformation.addressTemplate');
  }

  get billingAddressFormGroup(): FormGroup { return this.data.form.get('billingAddress') as FormGroup; }
  get scontoFormGroup(): FormGroup { return this.data.form.get('sconto') as FormGroup; }
  get scontoRateControl(): FormControl { return this.data.form.get('sconto').get('rate') as FormControl; }
  get scontoPeriodControl(): FormControl { return this.data.form.get('sconto').get('period') as FormControl; }
  get bankAccountIdControl(): FormControl { return this.data.form.get('bankAccount').get('id') as FormControl; }
  get ibanControl(): FormControl { return this.data.form.get('iban') as FormControl; }
  get bicControl(): FormControl { return this.data.form.get('bic') as FormControl; }
  get paymentTermsValue(): string { return this.data.form.get('paymentTerms').value; }
  get dueWithinDays(): FormControl { return this.data.form.get('paymentDueWithin') as FormControl; }
  get paymentDueWithinDate(): FormControl { return this.data.form.get('paymentDueWithinDate') as FormControl; }
  get paymentDueWithinType(): FormControl { return this.data.form.get('paymentDueWithinType') as FormControl; }
  get vatDisabled(): FormControl { return this.data.form.get('vatDisabled') as FormControl; }
  get addressTemplateControl(): FormControl { return this.data.form.get('addressTemplate') as FormControl; }
  get nameControl(): FormControl { return this.data.form.get('name') as FormControl; }
  get smallBusiness(): FormControl { return this.data.form.get('smallBusiness') as FormControl; }

  get billingAddressAsText(): string {
    if (!this.data.form) { return ''; }

    const addressVal = this.billingAddressFormGroup.value;
    return this.convertAddressToString(addressVal, this.countries);
  }

  public get isCustomerSelected(): boolean {
    return !!get(this, 'salesOrder.properties.company.runpleId');
  }

  get isGeneralPartner(): boolean {
    return get(this, 'salesOrder.properties.partnerType') === CustomerTypeEnum.GENERAL;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
